/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/jsx-props-no-spreading */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useApi } from 'contexts/ApiHandler';
import { useConfig } from 'contexts/config';
import { get } from 'lodash';
import CodeSnippet from './CodeSnippet';
import { isExpandable } from '../utils/common';
import { getGeneratedOptionsForOperation, generateOperationContent } from '../code-snippets/CodeSnippetGenerator';

const defaultConfig = {
  requiresClientId: false,
  clientIdLocation: null,
  clientIdName: null,
  requiresClientSecret: false,
  clientSecretLocation: null,
  clientSecretName: null,
  requiresBasicAuth: false,
  requiresDigestAuth: false,
  requiresHobaAuth: false,
  requiresScramShaAuth: false,
  requiresVapidAuth: false,
  requiresNegotiateAuth: false,
  requiresMutualAuth: false,
  requiresOauth: false,
  parametersArray: [],
  externalApiKeys: {},
  soapAction: null,
  contenttype: null,
  accept: null,
  selectedSecurityOption: 0
};

function CodeSnippetsDisplay({ snippetType, snippetInfo }) {
  const { api, apiHandler } = useApi();

  const configContext = useConfig();
  const config = useMemo(() => ({ ...defaultConfig, ...configContext, ...get(snippetInfo, 'config') }), [configContext, snippetInfo]);
  const codeLanguages = get(config, 'options.languages');
  const defaultCodeLanguage = get(config, 'options.defaultLanguage');

  const [codeLanguage, updateCodeLanguage] = useState(defaultCodeLanguage);
  const [generating, updateGenerating] = useState(true);
  const [operationSnippets, updateOperationSnippets] = useState([]);
  const [errorText, updateErrorText] = useState(null);
  const _isMounted = useRef(false);

  useEffect(() => {
    _isMounted.current = true;
    return () => {
      _isMounted.current = false;
    };
  }, []);

  const handleCodeSnippet = (data, error) => {
    if (_isMounted.current) {
      if (data) {
        updateGenerating(false);
        updateOperationSnippets(data);
      } else if (error) {
        updateGenerating(false);
        updateErrorText(error);
      }
    }
  };

  useEffect(() => {
    if (snippetInfo && api) {
      let generatorOptions = null;

      if (snippetType === 'operation' && snippetInfo.operation && snippetInfo.pathName) {
        const { operation, pathName, method, maxDepth } = snippetInfo;
        generatorOptions = getGeneratedOptionsForOperation(config, operation, pathName, method, codeLanguages, maxDepth, api, apiHandler);
      }

      if (generatorOptions && snippetType === 'operation') {
        handleCodeSnippet(generateOperationContent(generatorOptions).snippets);
      }
    } else {
      handleCodeSnippet(null, 'Invalid snippet configuration');
    }
  }, [snippetInfo, snippetType, config, api, apiHandler, codeLanguages]);

  useEffect(() => {
    if (_isMounted.current && codeLanguages && (!codeLanguages.includes(codeLanguage) || (!codeLanguage && !defaultCodeLanguage))) {
      updateCodeLanguage(codeLanguages[0]);
    }
  }, [codeLanguage, defaultCodeLanguage, codeLanguages]);

  const changeCodeLanguage = (newValue) => {
    if (_isMounted.current) {
      updateCodeLanguage(newValue);
    }
  };

  let content;
  if (operationSnippets && operationSnippets[`${codeLanguage}Content`]) {
    content = operationSnippets[`${codeLanguage}Content`];
  } else if (errorText) {
    content = errorText;
  }

  const largeContent = isExpandable(content, 8);

  const theProps = {
    generating,
    availableCodeLanguages: codeLanguages,
    errorText,
    onChangeCodeLanguage: changeCodeLanguage,
    activeCodeLanguage: codeLanguage,
    showExpander: largeContent,
    content,
    highlight: true
  };

  return <CodeSnippet {...theProps} />;
}

CodeSnippetsDisplay.propTypes = {
  operation: PropTypes.object,
  pathName: PropTypes.string,
  method: PropTypes.string
};

export default CodeSnippetsDisplay;
